import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { notification } from 'antd';
import { FiCheck } from 'react-icons/fi';
import PropertyService from 'service/PropertyService';
import { reviewPathAfterEdit } from 'library/helpers/importEditFlow';
import { setImages, setThumbnail } from 'features/placeSlice';
import { IMAGE_CATEGORY_OPTIONS } from 'settings/constant';
import { OnboardingShell, ob } from 'components/Onboarding';

type ImageUrlList = Record<string, string[]>;

interface Photo {
  url: string;
  category: string;
}

const categoryLabel = (key: string): string =>
  (IMAGE_CATEGORY_OPTIONS as Record<string, string>)[key] || key;

const isUsableId = (id: unknown): boolean =>
  id !== null && id !== undefined && id !== '' && id !== 'null' && id !== 'undefined';

/** After the photos are uploaded, the host picks which one is the listing's thumbnail. */
const ChooseThumbnail: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);
  const hostPropertyId = query.get('id');
  const isEditMode = isUsableId(hostPropertyId) || query.get('edit') === '1';

  const [photos, setPhotos] = useState<Photo[]>([]);
  const [imageUrlList, setImageUrlList] = useState<ImageUrlList>({});
  const [savedThumbUrl, setSavedThumbUrl] = useState<string | undefined>();
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  const resolvePropertyId = (): string | null => {
    if (isUsableId(hostPropertyId)) return hostPropertyId as string;
    const fromLs = localStorage.getItem('newPropertId') || localStorage.getItem('id');
    return isUsableId(fromLs) ? (fromLs as string) : null;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const pid = resolvePropertyId();
    if (!pid) {
      setLoaded(true);
      return undefined;
    }
    let cancelled = false;
    PropertyService.getPhotos(pid)
      .then((res: any) => {
        if (cancelled) return;
        const list: ImageUrlList = res?.result?.imageUrlList || {};
        const seen = new Set<string>();
        const all: Photo[] = [];
        Object.keys(list).forEach((category) => {
          (list[category] || []).forEach((url) => {
            if (url && !seen.has(url)) {
              seen.add(url);
              all.push({ url, category });
            }
          });
        });
        const thumb = res?.result?.thumbnailUrl || res?.result?.thumbnail || {};
        const thumbUrl = Object.values(thumb)[0] as string | undefined;
        setImageUrlList(list);
        setPhotos(all);
        setSavedThumbUrl(thumbUrl);
        setSelectedUrl(thumbUrl && seen.has(thumbUrl) ? thumbUrl : all[0]?.url ?? null);
      })
      .catch(() => {
        if (!cancelled) notification.error({ message: 'Could not load your photos. Please try again.' });
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const photosPath = (): string => {
    const pid = resolvePropertyId();
    return isEditMode && pid ? `/host/photo-upload?id=${pid}&edit=1` : '/host/photo-upload';
  };

  const saveAndContinue = async (): Promise<void> => {
    const pid = resolvePropertyId();
    if (!pid || !selectedUrl) return;
    setSaving(true);
    try {
      const category = photos.find((p) => p.url === selectedUrl)?.category;
      if (category && selectedUrl !== savedThumbUrl) {
        const thumbnailUrl = { [category]: selectedUrl };
        const res: any = await PropertyService.updatePhotos({}, imageUrlList, {}, thumbnailUrl, [], pid);
        if (!res?.result) {
          notification.error({
            message: 'Could not save your thumbnail',
            description: 'Please try again.',
          });
          return;
        }
        dispatch(setThumbnail(thumbnailUrl));
        dispatch(setImages({ imageUrlList, thumbnail: thumbnailUrl }));
      }
      if (isEditMode) {
        navigate(reviewPathAfterEdit(pid));
      } else {
        navigate('/host/property-listing');
      }
    } catch (_) {
      notification.error({
        message: 'Could not save your thumbnail',
        description: 'An error occurred, please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <OnboardingShell
      section={4}
      title="Choose your thumbnail"
      intro="This is the photo guests see first in search results. Pick the one that shows your property best."
      wide
      onBack={() => navigate(photosPath())}
      onContinue={saveAndContinue}
      continueLabel={isEditMode ? 'Update' : 'Continue'}
      continueLoading={saving}
      continueDisabled={!selectedUrl}
      pending={loaded && photos.length === 0 ? ['Add photos first, then choose a thumbnail.'] : []}
    >
      {!loaded ? null : (
        <Grid>
          {photos.map((photo) => {
            const selected = photo.url === selectedUrl;
            return (
              <Tile
                key={photo.url}
                type="button"
                $selected={selected}
                aria-pressed={selected}
                onClick={() => setSelectedUrl(photo.url)}
              >
                <img src={photo.url} alt={`${categoryLabel(photo.category)} photo`} />
                {selected ? (
                  <span className="badge">
                    <FiCheck size={12} strokeWidth={3} /> Thumbnail
                  </span>
                ) : null}
                <span className="room">{categoryLabel(photo.category)}</span>
              </Tile>
            );
          })}
        </Grid>
      )}
    </OnboardingShell>
  );
};

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  @media (min-width: 640px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const Tile = styled.button<{ $selected?: boolean }>`
  position: relative;
  min-width: 0;
  padding: 0;
  overflow: hidden;
  border: 2px solid ${({ $selected }) => ($selected ? ob.primary : ob.border)};
  border-radius: 16px;
  background: #fff;
  box-shadow: ${({ $selected }) =>
    $selected ? '0 0 0 3px rgba(255,113,15,0.18)' : '0 1px 2px rgba(26,26,26,0.04)'};
  cursor: pointer;
  transition: all 140ms ease;
  &:hover {
    border-color: ${ob.primary};
  }
  img {
    display: block;
    width: 100%;
    aspect-ratio: 4 / 3;
    object-fit: cover;
  }
  .badge {
    position: absolute;
    top: 8px;
    left: 8px;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 999px;
    background: ${ob.primary};
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .room {
    position: absolute;
    left: 8px;
    bottom: 8px;
    max-width: calc(100% - 16px);
    overflow: hidden;
    padding: 2px 8px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.92);
    font-size: 11px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export default ChooseThumbnail;
